import drawerTabs from '@/views/dashboard/config/drawerTabs';
import { Drawer } from '@/views/interfaces/vuetify/drawer.interface';
import { isAllowedPaletteGroup, PALETTE_ALLOWED_GROUPS, PALETTE_GROUP_LABELS } from './catalogConfig';
import { PaletteItem } from './types';

function userHasPermission(scope: string | undefined, scopes: string[], isAdmin: boolean): boolean {
  if (!scope || isAdmin) {
    return true;
  }

  if (scope.indexOf('*') !== -1) {
    return scopes.some((userScope: string) => userScope.match(new RegExp(scope)) !== null);
  }

  return scopes.indexOf(scope) !== -1;
}

function childPath(group: string, child: { to?: string; toFixed?: string }): string {
  if (child.toFixed) {
    return child.toFixed;
  }
  if (child.to !== undefined) {
    return `${group}${child.to}`;
  }
  return group;
}

function humanizeKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

/**
 * Flatten drawer tabs into absolute jump targets for the operator catalog only,
 * filtered by the caller's scopes.
 */
export function buildPageCatalog(scopes: string[], isAdmin: boolean): PaletteItem[] {
  const drawers: Drawer[] = drawerTabs();
  const items: PaletteItem[] = [];

  drawers.forEach((drawer) => {
    if (!isAllowedPaletteGroup(drawer.group)) {
      return;
    }
    if (!userHasPermission(drawer.scope, scopes, isAdmin)) {
      return;
    }

    const groupLabel = PALETTE_GROUP_LABELS[drawer.group] || drawer.title;

    (drawer.children || []).forEach((child) => {
      if (!userHasPermission(child.scope, scopes, isAdmin)) {
        return;
      }

      const path = childPath(drawer.group, child as { to?: string; toFixed?: string });
      const titleKey = child.title;
      const groupKey = drawer.title;

      items.push({
        id: `page:${path}`,
        kind: 'page',
        label: titleKey,
        description: path,
        icon: child.icon || drawer.icon || 'mdi-file-document-outline',
        path,
        keywords: [
          titleKey,
          groupKey,
          groupLabel,
          humanizeKey(titleKey),
          humanizeKey(groupKey),
          path,
          drawer.group,
        ].map((value) => String(value).toLowerCase()),
        meta: {
          titleKey,
          groupKey,
          groupPath: drawer.group,
          groupLabel,
        },
      });
    });
  });

  return items.sort((a, b) => {
    const ga = String((a.meta && a.meta.groupPath) || '');
    const gb = String((b.meta && b.meta.groupPath) || '');
    const ia = PALETTE_ALLOWED_GROUPS.indexOf(ga);
    const ib = PALETTE_ALLOWED_GROUPS.indexOf(gb);
    if (ia !== ib) {
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    }
    return String(a.path || '').localeCompare(String(b.path || ''));
  });
}
